import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { Auth } from "aws-amplify";
import { RedirectUri } from "./components/enumStrings";
import { setContext } from "@apollo/client/link/context";
import jwt_decode from "jwt-decode";
import * as Sentry from "@sentry/react";
import { ErrorCodeEnum } from "./graphql/types";

const uri =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

const getNewAccessToken = async (): Promise<string | undefined> => {
  try {
    await Auth.currentSession();
    const token = await Auth.currentAuthenticatedUser(); // add method to get token here
    return token.signInUserSession.accessToken.jwtToken;
  } catch (e) {
    console.log("Error getting access token for apollo client:", e);
  }

  return undefined;
};

const getApolloClient = async (
  redirectToError: () => void
): Promise<ApolloClient<NormalizedCacheObject>> => {
  const errorLink = onError((e) => {
    const urlPath = window.location.pathname;
    console.log("Caught Apollo Client Error", e);

    Sentry.withScope((scope) => {
      const { operation, graphQLErrors, networkError } = e;
      scope.setTransactionName(operation.operationName);
      scope.setContext("apolloGraphQLOperation", {
        operationName: operation.operationName,
        variables: operation.variables,
        extensions: operation.extensions,
      });

      graphQLErrors?.forEach((error) => {
        Sentry.captureMessage(error.message, {
          level: "error",
          fingerprint: ["{{ default }}", "{{ transaction }}"],
          contexts: {
            apolloGraphQLError: {
              error,
              message: error.message,
              extensions: error.extensions,
            },
          },
        });
      });

      if (networkError) {
        Sentry.captureMessage(networkError.message, {
          level: "error",
          contexts: {
            apolloNetworkError: {
              error: networkError,
              extensions: (networkError as any).extensions,
            },
          },
        });
      }
    });

    // if blacklist ting
    if (
      (e?.response?.errors || [{ message: "" }])[0]?.message ===
        ErrorCodeEnum.DisallowAccessTokenWithUserAuthYoungerThanUtcMilli ||
      (e?.response?.errors || [{ message: "" }])[0]?.message ===
        ErrorCodeEnum.DisallowAccessTokenPermanently ||
      (e?.response?.errors || [{ message: "" }])[0]?.message ===
        ErrorCodeEnum.DisallowUserTypeAccess
    ) {
      if (urlPath !== RedirectUri.logout) {
        window.location.href = RedirectUri.logout;
        return;
      }
    }

    if (
      urlPath !== RedirectUri.errorGeneral &&
      urlPath !== RedirectUri.support // ignoring this to prevent checking for graphql errors on the support page
    ) {
      window.location.href = RedirectUri.errorGeneral;
    }
  });

  let accessToken = await getNewAccessToken();

  const authLink = setContext(async (operation, previousContext) => {
    // refresh token if it's expired bruv and not a guest
    if (
      accessToken &&
      // eslint-disable-next-line
      // @ts-ignore
      Date.now() >= (jwt_decode(accessToken).exp as number) * 1000
    ) {
      accessToken = await getNewAccessToken();
    }

    let apiAuthorization = accessToken ? `Bearer ${accessToken}` : "";

    // attach access token for the manual requests made during setting a permanent password while using a temporary password
    if (
      previousContext &&
      previousContext?.customAccessTokenForSettingHasSignedUp &&
      previousContext?.customAccessTokenForSettingHasSignedUp?.length > 0
    ) {
      apiAuthorization = `Bearer ${previousContext.customAccessTokenForSettingHasSignedUp}`;
    }

    return {
      ...previousContext, // ensure previous context is passed along to sustain cache and all the other apollo graphql goodies (without passing this everything breaks)
      headers: {
        authorization: apiAuthorization,
      },
    };
  });

  const httpLink = new HttpLink({
    uri,
  });

  const link = ApolloLink.from([errorLink, authLink, httpLink]);

  const client = new ApolloClient({
    link,
    cache: new InMemoryCache(),
  });

  return client;
};

export default getApolloClient;
